<template>
  <div class="robofinder-question-item" v-if="isActive">
    <div class="question-header">
      <div class="question-number">Q{{ step }}.</div>
      <div class="question-text">{{ question }}</div>
    </div>

    <div class="question-options">
      <div
        v-for="option in options"
        :key="option.id"
        class="option-item"
        :class="{ 'is-selected': selectedValue == option.id }"
        @click="handleSelect(option.id)"
      >
        <div class="option-radio">
          <div class="radio-outer">
            <div v-if="selectedValue == option.id" class="radio-inner"></div>
          </div>
        </div>
        <img v-if="option.icon_url" :src="option.icon_url" class="option-icon" />
        <div class="option-text">
          {{ getOptionName(option) }}
        </div>
      </div>
    </div>

    <div class="question-navigation" v-if="showNavigation && (canGoBack || showEmailResults || canSkip || isRequired)">
      <button
        v-if="canGoBack"
        class="back-button"
        @click="handleBack"
      >
        <img src="/images/left_switch.png" class="nav-icon" alt="Back">
      </button>
      <div v-else class="back-spacer"></div>
      <div class="center-content">
        <a v-if="showEmailResults" href="#" class="email-link" @click.prevent="handleEmailResults">
          {{ getEmailResultsText() }}
        </a>
        <div v-if="showRequiredError" class="required-error">
          {{ getRequiredText() }}
        </div>
      </div>
      <button
        v-if="canSkip && showNavigation"
        class="next-button"
        @click="handleSkip"
      >
        <img src="/images/right_switch.png" class="nav-icon" alt="Next">
      </button>
      <button
        v-else-if="isRequired && showNavigation"
        class="next-button"
        @click="handleRequiredClick"
      >
        <img src="/images/right_switch.png" class="nav-icon" alt="Next">
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    step: {
      type: Number,
      required: true
    },
    question: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    selectedValue: {
      type: [String, Number],
      default: null
    },
    isActive: {
      type: Boolean,
      default: false
    },
    canSkip: {
      type: Boolean,
      default: false
    },
    canGoBack: {
      type: Boolean,
      default: false
    },
    showNavigation: {
      type: Boolean,
      default: true
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    resultCount: {
      type: Number,
      default: null
    },
    translations: {
      type: Object,
      default: () => ({})
    },
    locale: {
      type: String,
      default: 'en'
    }
  },

  data() {
    return {
      showRequiredError: false
    };
  },

  computed: {
    showEmailResults() {
      return this.resultCount !== null && this.resultCount >= 1 && this.resultCount <= 20;
    }
  },

  methods: {
    handleSelect(value) {
      this.$emit('select', value);
    },

    handleSkip() {
      this.$emit('skip');
    },

    handleBack() {
      this.$emit('back');
    },

    handleClose() {
      this.$emit('close');
    },

    handleEmailResults() {
      this.$emit('email-results');
    },

    getOptionName(option) {
      // Use locale prop passed from Rails via parent components
      const isJapanese = this.locale === 'ja' || this.locale.startsWith('ja-');
      return isJapanese ? option.name_ja : option.name_en;
    },

    getSelectedOptionName() {
      if (!this.selectedValue) return '';

      const selectedOption = this.options.find(
        opt => opt.id === parseInt(this.selectedValue)
      );

      if (!selectedOption) return '';

      return this.getOptionName(selectedOption);
    },

    getEmailResultsText() {
      // Use translations passed from Rails via props
      return this.translations?.questionnaire?.email_results || 'Email results';
    },

    handleRequiredClick() {
      // Show required error message
      this.showRequiredError = true;
    },

    getRequiredText() {
      return this.translations?.questionnaire?.required_question || 'この質問は必須です';
    }
  }
}
</script>

<style lang="scss" scoped>
.robofinder-question-item {
  .question-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;

    .question-number {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-right: 8px;
      flex-shrink: 0;
    }

    .question-text {
      flex: 1;
      font-size: 16px;
      font-weight: 500;
      color: #333;
      line-height: 1.4;
    }
  }

  .question-options {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .option-item {
      display: flex;
      align-items: center;
      padding: 16px;
      border: 2px solid #e0e0e0;
      border-radius: 0;
      cursor: pointer;
      transition: all 0.2s ease;
      background-color: #fff;

      &:hover {
        border-color: #4a4a4a;
        background-color: #fafafa;
      }

      &.is-selected {
        border-color: #4a4a4a;
        background-color: #f0f0f0;
      }

      .option-radio {
        margin-right: 12px;
        flex-shrink: 0;

        .radio-outer {
          width: 20px;
          height: 20px;
          border: 2px solid #999;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: border-color 0.2s ease;

          .radio-inner {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: #4a4a4a;
          }
        }
      }

      &.is-selected .option-radio .radio-outer {
        border-color: #4a4a4a;
      }

      .option-icon {
        width: 24px;
        height: 24px;
        margin-right: 8px;
        object-fit: contain;
        flex-shrink: 0;
      }

      .option-text {
        flex: 1;
        font-size: 15px;
        color: #333;
        line-height: 1.4;
      }
    }
  }

  .question-navigation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;

    .back-button,
    .next-button {
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 0;
      background-color: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
      padding: 0;

      .nav-icon {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .back-button {
      .nav-icon {
        content: url('/images/left_switch.png');
      }

      &:hover .nav-icon {
        content: url('/images/left_switch_hover.png');
      }
    }

    .next-button {
      .nav-icon {
        content: url('/images/right_switch.png');
      }

      &:hover .nav-icon {
        content: url('/images/right_switch_hover.png');
      }
    }

    .back-button {
      visibility: visible;
    }

    // 戻るボタンがない場合のスペーサー
    .back-spacer {
      width: 40px;
      flex-shrink: 0;
    }

    .center-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    .email-link {
      color: #4a90e2;
      text-decoration: underline;
      font-size: 15px;
      cursor: pointer;

      &:hover {
        color: #357abd;
      }
    }

    .required-error {
      color: #d32f2f;
      font-size: 14px;
      font-weight: 500;
    }

    // リンクがない場合のスペーサー
    .email-spacer {
      flex: 1;
    }

    .next-button {
      visibility: visible;
    }
  }
}
</style>
